/**
 * Manages page visibility state and pauses data fetching when tab is hidden
 * This dramatically reduces unnecessary Supabase egress costs
 */

type VisibilityCallback = (isVisible: boolean) => void;

class VisibilityManager {
  private callbacks: Set<VisibilityCallback> = new Set();
  private isVisible: boolean = true;
  private initialized: boolean = false;
  private lastActiveTime: number = Date.now();
  private activityCheckInterval: number | null = null;

  constructor() {
    if (typeof document !== 'undefined') {
      this.isVisible = document.visibilityState === 'visible';
    }
  }

  initialize() {
    if (this.initialized || typeof document === 'undefined') {
      return;
    }

    const handleVisibilityChange = () => {
      const wasVisible = this.isVisible;
      this.isVisible = document.visibilityState === 'visible';

      if (wasVisible !== this.isVisible) {
        console.log(`[VisibilityManager] Tab ${this.isVisible ? 'visible' : 'hidden'}`);
        this.callbacks.forEach(callback => callback(this.isVisible));
      }

      if (this.isVisible) {
        this.lastActiveTime = Date.now();
      }
    };

    const handleUserActivity = () => {
      this.lastActiveTime = Date.now();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('mousemove', handleUserActivity, { passive: true });
    document.addEventListener('keydown', handleUserActivity, { passive: true });
    document.addEventListener('click', handleUserActivity, { passive: true });
    document.addEventListener('scroll', handleUserActivity, { passive: true });
    document.addEventListener('touchstart', handleUserActivity, { passive: true });

    this.initialized = true;
  }

  subscribe(callback: VisibilityCallback): () => void {
    this.callbacks.add(callback);

    return () => {
      this.callbacks.delete(callback);
    };
  }

  getIsVisible(): boolean {
    return this.isVisible;
  }

  getLastActiveTime(): number {
    return this.lastActiveTime;
  }

  isRecentlyActive(thresholdMs: number = 300000): boolean {
    return Date.now() - this.lastActiveTime < thresholdMs;
  }
}

export const visibilityManager = new VisibilityManager();

// Initialize on module load
if (typeof document !== 'undefined') {
  visibilityManager.initialize();
}
